import { type JSONObject, Temporal } from '@imago/api-client'
import { type DocBlock } from './_Doc.ts'
import { type EMRKindID, type EMRTypeID } from './_EMR.ts'
import { TokenStream } from './_parsing.ts'

export const Profile_ID = 'imago.user-profile' as EMRTypeID & EMRKindID

export interface UserProfileContent {
	shortName: string
	pronouns: string

	legalName: string
	dateOfBirth: Temporal.PlainDate
}

export interface UserProfileContentJSON extends JSONObject {
	shortName: string
	pronouns: string

	legalName: string
	dateOfBirth: Temporal.PlainDate_ISO
}

export function serializeUserProfileContent(userProfile: UserProfileContent | null | undefined): UserProfileContentJSON | null | undefined {
	return !userProfile ? userProfile : {
		...userProfile,
		dateOfBirth: userProfile.dateOfBirth.toString()
	}
}

export function deserializeUserProfileContent(userProfile: UserProfileContentJSON): UserProfileContent {
	return {
		...userProfile,
		dateOfBirth: Temporal.PlainDate.from(userProfile.dateOfBirth)
	}
}

export function parseUserProfile(md: DocBlock[]): UserProfileContent {
	const blocks = md.map(block => ({...block, text: block.text.trim()})).filter(block => block.text.length > 0)
	const ts = new TokenStream(blocks[Symbol.iterator]())

	ts.match("TITLE", "User profile")
	const content = ts.consumePlainContent()

	const profile = {} as UserProfileContent

	for (const line of content.split('\n')) {
		if (!line.trim()) continue
		const sepPos = line.indexOf(':')
		const key = line.substring(0, sepPos).trim()
		const value = line.substring(sepPos + 1).trim()

		if (key === 'Short name') {
			profile.shortName = value
		} else if (key === 'Pronouns') {
			profile.pronouns = value
		} else if (key === 'Legal name') {
			profile.legalName = value
		} else if (key === 'Date of birth') {
			profile.dateOfBirth = Temporal.PlainDate.from(value)
		} else {
			console.warn(`Unknown profile key: ${key}`)
		}
	}

	return profile
}

export function docifyUserProfile(profile: UserProfileContent): DocBlock[] {
	return [
		{ class: 'TITLE', text: "User profile" },
		{ class: 'NORMAL_TEXT', text: `Short name: ${profile.shortName}` },
		{ class: 'NORMAL_TEXT', text: `Pronouns: ${profile.pronouns}` },
		{ class: 'NORMAL_TEXT', text: `Legal name: ${profile.legalName}` },
		{ class: 'NORMAL_TEXT', text: `Date of birth: ${profile.dateOfBirth.toString()}` },
	]
}
